/** @jsxImportSource @emotion/react */

import { jsx } from '@emotion/react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { t } from '@sportninja/common/i18n'
import { getCustomerSupportUrl } from '@sportninja/common/utils/getClientUrls'

import PageMenu from './PageMenu'
import PageTitle from './PageTitle'
import Helmet from './Helmet'
import LoadingSpinner from './LoadingSpinner'
import ErrorScreen from './ErrorScreen'

// List of sub-pages allowed in iframe mode
const IFRAME_PAGE_FILTER = [
  'bracket',
  'schedule',
  'standings',
  'statistics',
  'teams',
  'details',
  'home roster',
  'visitor roster',
  'timeline',
  'scoresheet',
  'sub orgs',
  'competitions',
  'roster',

  // Tricksy Canlan translation values...
  'sub leagues',
  'seasons',
]

const PageWrap = ({
  actions,
  bottomTitle,
  children,
  className,
  error,
  errorTitle,
  errorMessage,
  extraOffset,
  inFrame,
  isPrivate,
  loaded,
  pages: _pages,
  pageHeader,
  picture,
  pictureSettings,
  secondPicture,
  showPicturePlaceholder,
  title,
  topTitle,
  navigationDisabled,
}) => {
  const privateEntityError =
    (error && error.status === 403) ||
    (error && error.status !== 404 && isPrivate)

  if (privateEntityError) {
    return (
      <div className={className}>
        <Helmet title={t('Web:private')} />
        <ErrorScreen
          onButtonPress={() =>
            window.open(
              getCustomerSupportUrl(),
              '_blank',
              'noopener,noreferrer'
            )
          }
          buttonText='Contact Support'
          title='Access Denied'
          message={t('common:xIsPrivate', { x: 'game' })}
        />
      </div>
    )
  }

  if (error) {
    return (
      <div className={className}>
        <Helmet
          title={(error.status && `HTTP ${error.status}`) || t('common:error')}
        />
        <ErrorScreen
          title={errorTitle}
          message={errorMessage ?? error.message}
        />
      </div>
    )
  }

  const titleString = title || `${t('common:loading')}...`

  const pages = inFrame
    ? _pages.filter((p) => {
        return !p.isOrange && IFRAME_PAGE_FILTER.includes(p.title.toLowerCase())
      })
    : _pages

  return (
    <div className={className}>
      {titleString && <Helmet title={titleString} />}
      {pageHeader || (
        <PageTitle
          actions={inFrame ? [] : actions}
          bottomTitle={bottomTitle}
          picture={picture}
          pictureSettings={pictureSettings}
          secondPicture={secondPicture}
          showPicturePlaceholder={showPicturePlaceholder}
          title={titleString}
          topTitle={topTitle}
        />
      )}
      {pages && (
        <PageMenu
          inFrame={inFrame}
          extraOffset={extraOffset}
          pages={pages}
          disabled={navigationDisabled}
          actions={actions}
        />
      )}
      {loaded ? children : <LoadingSpinner />}
    </div>
  )
}

PageWrap.propTypes = {
  actions: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
  bottomTitle: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  children: PropTypes.node.isRequired,
  css: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  errorTitle: PropTypes.string,
  errorMessage: PropTypes.string,
  extraOffset: PropTypes.number,
  inFrame: PropTypes.bool,
  isPrivate: PropTypes.bool,
  loaded: PropTypes.bool.isRequired,
  pages: PropTypes.array,
  pageHeader: PropTypes.node,
  pageTitle: PropTypes.string,
  picture: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  pictureSettings: PropTypes.object, // Settings for the Picture component
  secondPicture: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  showPicturePlaceholder: PropTypes.bool,
  title: PropTypes.string,
  topTitle: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
}

const mapStateToProps = (state) => {
  return {
    inFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(PageWrap)
